import { toRaw } from 'vue';
import dtoHashes from '@/lib/dto-hashes.json';
import { queueSnapshotWrite, type Snapshot } from '@lib/snapshot-cache';
import { useUser } from '@/store/user';
import type { DtoName } from '@/store/global-store-base';
import type { Stores } from '@/store';

// Warm boot: seed the global stores from IndexedDB snapshots so the app renders
// immediately, then let the normal full reload revalidate in the background.
// See lib/snapshot-cache.ts for the persistence layer and app.js for the boot wiring.

type SeedableStore = {
  loadAll: () => Promise<unknown>;
  seed: (items: unknown) => void;
  dtoName: DtoName;
};

// useUser() is untyped JS; narrow to what snapshot keying needs. Null while logged out.
export function snapshotIdentity(): { userId: string; brandId: number } | null {
  const user = useUser() as { current?: { id?: string } | null; brandId?: number | null };
  if (!user.current?.id || !user.brandId) return null;
  return { userId: user.current.id, brandId: user.brandId };
}

function asLoadable(store: unknown): { loadAll: () => Promise<unknown> } | null {
  if (typeof store !== 'object' || store === null) return null;
  return typeof (store as SeedableStore).loadAll === 'function' ? (store as SeedableStore) : null;
}

function asSeedable(store: unknown): SeedableStore | null {
  const loadable = asLoadable(store);
  if (!loadable) return null;
  const candidate = loadable as SeedableStore;
  return typeof candidate.seed === 'function' && typeof candidate.dtoName === 'string' ? candidate : null;
}

// Stores with bespoke per-id caches (products, articles, components) implement this trio
// to participate in snapshot persistence. Unlike the loadAll-based global stores they are
// not revalidated wholesale on boot — seeded entries refresh through their normal SWR
// ensure* access paths and SignalR listeners — and they never block rendering.
type SnapshotCapableStore = {
  snapshotDto: DtoName;
  snapshotData: () => unknown;
  seedSnapshot: (data: unknown) => void;
  $subscribe: (callback: () => void, options?: { detached?: boolean }) => void;
};

function asSnapshotCapable(store: unknown): SnapshotCapableStore | null {
  if (typeof store !== 'object' || store === null) return null;
  const candidate = store as SnapshotCapableStore;
  return typeof candidate.snapshotData === 'function' &&
    typeof candidate.seedSnapshot === 'function' &&
    typeof candidate.snapshotDto === 'string'
    ? candidate
    : null;
}

// Seeds every store that has a valid snapshot (right DTO schema hash, not expired).
// Returns the names of loadAll-capable stores that were NOT seeded — the caller must
// load those in the foreground before declaring the app fully loaded. On a cold boot
// (no snapshots) that is simply every loadable store.
export function seedStores(
  stores: Stores,
  snapshots: Record<string, Snapshot>
): { unseeded: string[]; seededCount: number } {
  const unseeded: string[] = [];
  let seededCount = 0;

  for (const [name, store] of Object.entries(stores)) {
    const snapshot = snapshots[name];

    // Per-id cache stores: seed opportunistically, they never block boot.
    const snapshotCapable = asSnapshotCapable(store);
    if (snapshotCapable) {
      if (
        snapshot &&
        typeof snapshot.data === 'object' &&
        snapshot.data !== null &&
        snapshot.schemaHash === dtoHashes[snapshotCapable.snapshotDto]
      ) {
        snapshotCapable.seedSnapshot(snapshot.data);
      }
      continue;
    }

    if (!asLoadable(store)) continue;

    const seedable = asSeedable(store);
    if (seedable && snapshot && Array.isArray(snapshot.data) && snapshot.schemaHash === dtoHashes[seedable.dtoName]) {
      seedable.seed(snapshot.data);
      seededCount++;
    } else {
      unseeded.push(name);
    }
  }

  return { unseeded, seededCount };
}

// Persist every seedable store's list whenever a loadAll replaces it. Idempotent;
// user/brand are resolved at write time so post-login in-page loads persist too.
let persistenceRegistered = false;
export function registerPersistence(stores: Stores) {
  if (persistenceRegistered) return;
  persistenceRegistered = true;

  for (const [name, store] of Object.entries(stores)) {
    // Per-id cache stores persist on any state mutation (debounced/batched in
    // snapshot-cache); detached so the subscription outlives the mounting component.
    const snapshotCapable = asSnapshotCapable(store);
    if (snapshotCapable) {
      snapshotCapable.$subscribe(
        () => {
          const identity = snapshotIdentity();
          if (!identity) return;
          queueSnapshotWrite(
            identity.userId,
            identity.brandId,
            name,
            snapshotCapable.snapshotData(),
            dtoHashes[snapshotCapable.snapshotDto]
          );
        },
        { detached: true }
      );
      continue;
    }

    const seedable = asSeedable(store);
    const onReplaced = (store as { onReplaced?: (cb: (items: unknown[]) => void) => void }).onReplaced;
    if (!seedable || typeof onReplaced !== 'function') continue;

    onReplaced((items: unknown[]) => {
      const identity = snapshotIdentity();
      if (!identity) return;
      // toRaw: IndexedDB's structured clone can't handle Vue's reactive proxies
      queueSnapshotWrite(identity.userId, identity.brandId, name, toRaw(items), dtoHashes[seedable.dtoName]);
    });
  }
}
